import {SingleLogoutService} from './single-logout.service';
import {Injectable, inject} from '@angular/core';
import {LoginResponse, OidcSecurityService} from 'angular-auth-oidc-client';
import {catchError, of, switchMap} from 'rxjs';
import {environment} from '../../../environments/environment';

@Injectable({providedIn: 'root'})
export class AuthSessionService {
  private readonly oidc = inject(OidcSecurityService);
  private readonly singleLogout = inject(SingleLogoutService);
  private leaving = false;

  initialize() {
    if (/\/(logout|thaid-callback)\/?$/.test(window.location.pathname)) {
      return of(undefined);
    }

    this.singleLogout.start();

    return this.oidc.checkAuth().pipe(
      catchError(err => {
        console.warn('OIDC checkAuth failed:', err);
        this.cleanupStaleUrlParams();
        const fallback: LoginResponse = {
          isAuthenticated: false,
          errorMessage: err?.message ?? 'checkAuth failed',
          userData: null,
          accessToken: '',
          idToken: '',
          configId: '',
        };
        return of(fallback);
      }),
      switchMap(result => {
        if (result.isAuthenticated || /\/callback\/?$/.test(window.location.pathname)) {
          return of(result);
        }
        return this.oidc.getRefreshToken().pipe(
          switchMap(token => token
            ? this.oidc.checkAuthIncludingServer().pipe(catchError(() => of(result)))
            : of(result)),
        );
      }),
    );
  }

  logout(): void {
    if (this.leaving) return;
    this.leaving = true;
    this.clearReturnUrl();
    let redirected = false;
    this.oidc.logoff(undefined, {
      customParams: {client_id: environment.clientId},
      urlHandler: url => {
        redirected = true;
        this.redirectTo(url);
      },
    }).subscribe({
      error: () => this.endLocalSession(),
      complete: () => { if (!redirected) this.endLocalSession(); },
    });
  }

  private cleanupStaleUrlParams(): void {
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.has('state') || url.searchParams.has('code') || url.searchParams.has('session_state')) {
        url.searchParams.delete('state');
        url.searchParams.delete('code');
        url.searchParams.delete('session_state');
        const cleanSearch = url.searchParams.toString() ? `?${url.searchParams.toString()}` : '';
        const cleanUrl = url.pathname + cleanSearch + url.hash;
        window.history.replaceState(window.history.state, document.title, cleanUrl);
      }
    } catch {
      // Storage or history API might be unavailable in certain environments
    }
  }

  private endLocalSession(): void {
    this.leaving = true;
    this.clearReturnUrl();
    this.oidc.logoffLocalMultiple();
    this.redirectTo(window.location.origin + '/ui/logout');
  }

  private clearReturnUrl(): void {
    try { sessionStorage.removeItem('acfs.auth.return-url'); } catch { /* Storage may be disabled. */ }
  }

  private redirectTo(url: string): void {
    window.location.assign(url);
  }
}
